// Tester profile — a saved device/testing setup, recorded once rather than
// re-entered on every APK test. A tester can save several (e.g. a personal
// Samsung phone + a shared test iPad) and pick which one they're using per
// test; devs reading test history can see exactly who tested on what
// hardware without the tester re-typing it each time.

export const TESTER_ROLES = [
  { value: "android_tester", label: "Android Tester" },
  { value: "ios_tester", label: "iOS Tester" },
  { value: "qa_manager", label: "QA Manager" },
] as const;

export type TesterRole = (typeof TESTER_ROLES)[number]["value"];

export const testerRoleLabel = (value: string): string =>
  TESTER_ROLES.find((r) => r.value === value)?.label ?? value;

/** One saved device/testing profile. A user can have several — keyed by
 *  `_id`, not `userId`, so multiple devices can coexist. */
export interface ITesterProfile {
  _id: string;
  userId: string;
  /** Short name to tell devices apart when a tester has more than one,
   *  e.g. "Personal Galaxy S23" / "Shared test iPad". */
  label: string;
  role: TesterRole;
  device: string;
  osVersion: string;
  screenSize: string;
  appBuildVersion: string;
  updatedAt: string;
}

export interface ITesterProfileInput {
  label: string;
  role: TesterRole;
  device: string;
  osVersion: string;
  screenSize: string;
  appBuildVersion: string;
}

export interface ITesterProfilesResponse {
  profiles: ITesterProfile[];
}

/** Snapshot of a tester's profile as it stood at the time a test was
 *  recorded — persisted on each test so history stays accurate even after
 *  the tester later updates/removes that profile. */
export interface ITesterProfileSnapshot {
  label: string;
  role: TesterRole;
  device: string;
  osVersion: string;
  screenSize: string;
  appBuildVersion: string;
}
